import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Bell } from "lucide-react";

const OPTIONS = [
    {
        key: "approval_requests",
        title: "Approval requests",
        desc: "Email the client when a new deliverable requires their approval.",
    },
    {
        key: "new_versions",
        title: "New versions",
        desc: "Email the client when you submit a revised version for approval.",
    },
    {
        key: "new_files",
        title: "New files",
        desc: "Email the client when a new file is added to their portal.",
    },
    {
        key: "project_progress",
        title: "Project progress",
        desc: "Email the client when progress, status or the next milestone changes.",
    },
    {
        key: "comment_notifications",
        title: "Comments",
        desc: "Email the other side when a new comment is added to a deliverable version.",
    },
];

const DEFAULTS = {
    approval_requests: true,
    new_versions: true,
    new_files: true,
    project_progress: true,
    comment_notifications: true,
};

export default function NotificationPrefsDialog({ open, onOpenChange, clientId, clientName }) {
    const [prefs, setPrefs] = useState(DEFAULTS);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !clientId) return;
        setLoading(true);
        api.get(`/clients/${clientId}/notification-prefs`)
            .then(({ data }) => setPrefs({ ...DEFAULTS, ...data }))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [open, clientId]);

    const toggle = async (key, value) => {
        const previous = prefs[key];
        setPrefs((p) => ({ ...p, [key]: value }));
        setSaving(true);
        try {
            const { data } = await api.patch(`/clients/${clientId}/notification-prefs`, { [key]: value });
            setPrefs({ ...DEFAULTS, ...data });
            toast.success(`Notifications ${value ? "enabled" : "disabled"}`, {
                description: OPTIONS.find((o) => o.key === key)?.title,
            });
        } catch (err) {
            setPrefs((p) => ({ ...p, [key]: previous }));
            toast.error("Could not save preference", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[520px] rounded-xl border-slate-200">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight inline-flex items-center gap-2">
                        <Bell className="w-4 h-4 text-[#C55A46]" /> Notification preferences
                    </DialogTitle>
                    <DialogDescription className="text-slate-500">
                        Choose which automatic emails <span className="text-slate-700 font-medium">{clientName}</span> receives.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="py-8 flex justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100 -mx-1">
                        {OPTIONS.map((o) => (
                            <li key={o.key} data-testid={`pref-row-${o.key}`}
                                className="flex items-start justify-between gap-4 py-4 px-1">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-slate-900">{o.title}</p>
                                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{o.desc}</p>
                                </div>
                                <Switch
                                    checked={!!prefs[o.key]}
                                    disabled={saving}
                                    onCheckedChange={(v) => toggle(o.key, v)}
                                    data-testid={`pref-toggle-${o.key}`}
                                />
                            </li>
                        ))}
                    </ul>
                )}

                <div className="pt-2 text-xs text-slate-400">
                    Changes save automatically. Events still appear in the Activity feed even when the email is disabled.
                </div>
            </DialogContent>
        </Dialog>
    );
}
